import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Play } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";

import { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { CONTENT_TYPES } from "@/types";
import { SHORTCUTS } from "@/utils/shortcuts";

export interface MessageSubmitI {
  onSend?: () => void;
  isSending?: boolean;
  isToolResponseMode?: boolean;
  /** Optional split-button segment rendered joined to the right of Send (e.g. the mock-tools menu). */
  secondaryAction?: ReactNode;
}

export interface MessageSubmitButtonPropsI extends MessageSubmitI {
  message: IMessageStore;
  className?: string;
  isSizeXS?: boolean;
}

const getButtonAnimation = (isSending: boolean, isAnimating: boolean) => {
  if (isSending) {
    return {
      scale: [1, 1.02, 1],
      transition: {
        repeat: Infinity,
        duration: 1.2,
        ease: "easeInOut",
      },
    };
  }

  if (isAnimating) {
    return {
      scale: [1, 1.03, 1],
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    };
  }

  return {};
};

export const MessageSubmitButton = ({
  onSend,
  isSending,
  isToolResponseMode,
  secondaryAction,
  message,
  className,
  isSizeXS = false,
}: MessageSubmitButtonPropsI) => {
  const [isAnimating, setIsAnimating] = useState(false);
  /**
   * Determines if the message is allowed to be sent.
   * The message is allowed to be sent if it is not sending,
   * message text is not empty OR has media.
   */
  const isAllowedToSend = useMemo(
    () =>
      !isSending &&
      !message.isEmptyMessage &&
      (message.content.length > 0 ||
        message.contentBlocks.some(block => block.type === CONTENT_TYPES.MEDIA)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSending, message.content.length, message.contentBlocks.length, message.isEmptyMessage],
  );

  const handleClick = useCallback(() => {
    if (!isAllowedToSend) {
      return;
    }

    setIsAnimating(true);
    onSend?.();
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAllowedToSend, onSend]);

  const idleActionText = isToolResponseMode ? "Submit" : "Send";
  const loadingActionText = isToolResponseMode ? "Submitting..." : "Sending...";
  const buttonText = isSending ? loadingActionText : idleActionText;
  const tooltipText = (() => {
    if (isSending) {
      return isToolResponseMode ? "Submitting tool response..." : "Sending message...";
    }

    if (!isAllowedToSend) {
      return "Write a message to send";
    }

    return `${isToolResponseMode ? "Submit tool response" : "Submit message"} (${SHORTCUTS.SUBMIT.displayWithPlus})`;
  })();

  return (
    <div className={cn("pointer-events-auto z-10 flex items-stretch", className)}>
      <BasicTooltip content={tooltipText}>
        <div className={cn(!isAllowedToSend && "opacity-70")}>
          <AnimatePresence>
            <motion.div
              initial={{ scale: 1 }}
              animate={getButtonAnimation(isSending || false, isAnimating)}
              whileTap={!isAllowedToSend ? undefined : { scale: 0.98 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 15,
              }}
            >
              <Button
                onClick={handleClick}
                disabled={!isAllowedToSend}
                size="sm"
                className={cn(
                  "rounded-md border text-xs font-medium shadow-sm transition-all duration-200 ease-in-out",
                  isSizeXS ? "h-7 min-w-0 gap-1.5 px-2" : "h-8 min-w-[88px] gap-1.5 px-2.5",
                  isToolResponseMode
                    ? "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100 hover:text-amber-800"
                    : "border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800",
                  isAllowedToSend ? "hover:brightness-105" : "cursor-not-allowed",
                  // Joined to the split-button segment on the right.
                  secondaryAction && "rounded-r-none border-r-0",
                )}
              >
                {isSending ? (
                  <Loader2
                    className={cn(isSizeXS ? "h-2.5 w-2.5" : "h-3 w-3", "animate-spin opacity-70")}
                  />
                ) : (
                  <Play className={cn(isSizeXS ? "h-2.5 w-2.5" : "h-3 w-3", "opacity-80")} />
                )}
                {!isSizeXS && <span className="whitespace-nowrap leading-none">{buttonText}</span>}
                {isAllowedToSend && !isSending && (
                  <kbd
                    className={cn(
                      "ml-0.5 select-none rounded border bg-white/70 font-mono font-medium leading-none opacity-80 transition-opacity duration-200 ease-in-out",
                      isSizeXS ? "px-1 py-px text-[10px]" : "px-1.5 py-px text-[11px]",
                      isToolResponseMode
                        ? "border-amber-200 text-amber-600"
                        : "border-blue-200 text-blue-600",
                    )}
                  >
                    {SHORTCUTS.SUBMIT.display}
                  </kbd>
                )}
              </Button>
            </motion.div>
          </AnimatePresence>
        </div>
      </BasicTooltip>
      {secondaryAction}
    </div>
  );
};
